// src/components/eventos/components/TarefaForm.js
import React, { useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';

const calcularDuracao = (horaInicio, horaFim) => {
    const [hI, mI] = horaInicio.split(':').map(Number);
    const [hF, mF] = horaFim.split(':').map(Number);
    let dur = (hF * 60 + mF) - (hI * 60 + mI);
    if (dur < 0) dur += 24 * 60;
    return dur;
};

/* ---- SVG Icons ---- */
const PlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);
const MinusIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const TarefaForm = ({ tarefaEditando, onAdicionarTarefa, onAtualizarTarefa, onCancelarEdicao }) => {
    const [tarefa, setTarefa] = useState({
        titulo: '', horaInicio: '', horaFim: '', responsavel: '', local: '',
        descricao: '', descricao_tipo: 'texto', descricao_items: [],
    });
    const [novoItem, setNovoItem] = useState('');
    const toast = useToast();

    const limpar = () => {
        setTarefa({ titulo: '', horaInicio: '', horaFim: '', responsavel: '', local: '', descricao: '', descricao_tipo: 'texto', descricao_items: [] });
        setNovoItem('');
    };

    useEffect(() => {
        if (tarefaEditando) {
            setTarefa({
                titulo: tarefaEditando.titulo,
                horaInicio: tarefaEditando.horaInicio,
                horaFim: tarefaEditando.horaFim,
                responsavel: tarefaEditando.responsavel || '',
                local: tarefaEditando.local || '',
                descricao: tarefaEditando.descricao || '',
                descricao_tipo: tarefaEditando.descricao_tipo || 'texto',
                descricao_items: tarefaEditando.descricao_items || [],
            });
        } else {
            limpar();
        }
    }, [tarefaEditando]);

    const validate = () => {
        if (!tarefa.titulo.trim()) {
            toast({ title: 'Título obrigatório', status: 'warning', duration: 2000 }); return false;
        }
        if (!tarefa.horaInicio || !tarefa.horaFim) {
            toast({ title: 'Horários obrigatórios', description: 'Informe hora de início e fim', status: 'warning', duration: 2000 }); return false;
        }
        if (calcularDuracao(tarefa.horaInicio, tarefa.horaFim) <= 0) {
            toast({ title: 'Horário inválido', description: 'A hora fim deve ser maior que a hora início', status: 'error', duration: 3000 }); return false;
        }
        return true;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const duracao = calcularDuracao(tarefa.horaInicio, tarefa.horaFim);
        const tarefaCompleta = {
            id: tarefaEditando?.id || crypto.randomUUID(),
            titulo: tarefa.titulo.trim(),
            horaInicio: tarefa.horaInicio, horaFim: tarefa.horaFim,
            responsavel: tarefa.responsavel.trim(), local: tarefa.local.trim(),
            duracao, status: tarefaEditando?.status || 'pendente',
            horaInicioReal: tarefaEditando?.horaInicioReal || null,
            horaFimReal: tarefaEditando?.horaFimReal || null,
            descricao: tarefa.descricao_tipo === 'texto' ? tarefa.descricao.trim() : '',
            descricao_tipo: tarefa.descricao_tipo,
            descricao_items: tarefa.descricao_tipo === 'lista' ? tarefa.descricao_items : [],
        };
        if (tarefaEditando) { onAtualizarTarefa(tarefaCompleta); } else { onAdicionarTarefa(tarefaCompleta); }
        limpar();
    };

    const handleAdicionarItem = () => {
        if (!novoItem.trim()) { toast({ title: 'Item vazio', status: 'warning', duration: 2000 }); return; }
        setTarefa(prev => ({ ...prev, descricao_items: [...prev.descricao_items, novoItem.trim()] }));
        setNovoItem('');
    };

    const inputStyle = {
        width: '100%', padding: '9px 12px', borderRadius: '8px',
        background: '#f8fafc', border: '1px solid #e2e8f0',
        color: '#1e293b', fontSize: '13px', fontFamily: 'inherit', outline: 'none',
        boxSizing: 'border-box', transition: 'border-color 0.15s',
    };
    const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' };

    return (
        <>
            <style>{`
                .tf-input::placeholder { color: #94a3b8; }
                .tf-input:focus { border-color: #2CCB90 !important; background: #f0fdf8 !important; }
                .tf-tipo-btn { padding:6px 14px; border-radius:7px; font-size:12px; font-weight:600; border:1px solid transparent; cursor:pointer; font-family:inherit; transition:all 0.15s; }
                .tf-tipo-btn.active { background:rgba(44,203,144,0.1); border-color:rgba(44,203,144,0.25); color:#2CCB90; }
                .tf-tipo-btn.inactive { background:#f8fafc; border-color:#e2e8f0; color:#64748b; }
                .tf-tipo-btn.inactive:hover { background:#f0fdf8; color:#1e293b; }
            `}</style>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', fontFamily: "'Inter', -apple-system, sans-serif", boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {/* Título */}
                    <div>
                        <label style={labelStyle}>Título da Tarefa *</label>
                        <input className="tf-input" style={inputStyle} value={tarefa.titulo}
                            onChange={e => setTarefa(p => ({ ...p, titulo: e.target.value }))}
                            placeholder="Ex: Abertura, Louvor, Pregação..." />
                    </div>

                    {/* Horários */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>Hora Início *</label>
                            <input className="tf-input" style={inputStyle} type="time" value={tarefa.horaInicio}
                                onChange={e => setTarefa(p => ({ ...p, horaInicio: e.target.value }))} />
                        </div>
                        <div>
                            <label style={labelStyle}>Hora Fim *</label>
                            <input className="tf-input" style={inputStyle} type="time" value={tarefa.horaFim}
                                onChange={e => setTarefa(p => ({ ...p, horaFim: e.target.value }))} />
                        </div>
                    </div>

                    {/* Responsável */}
                    <div>
                        <label style={labelStyle}>Responsável</label>
                        <input className="tf-input" style={inputStyle} value={tarefa.responsavel}
                            onChange={e => setTarefa(p => ({ ...p, responsavel: e.target.value }))}
                            placeholder="Nome do responsável pela tarefa" />
                    </div>

                    {/* Local */}
                    <div>
                        <label style={labelStyle}>Local (opcional)</label>
                        <input className="tf-input" style={inputStyle} value={tarefa.local}
                            onChange={e => setTarefa(p => ({ ...p, local: e.target.value }))}
                            placeholder="Ex: Sala principal, Palco..." />
                    </div>

                    {/* Detalhes */}
                    <div>
                        <label style={labelStyle}>Detalhes da Tarefa (opcional)</label>
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                            <button className={`tf-tipo-btn ${tarefa.descricao_tipo === 'texto' ? 'active' : 'inactive'}`}
                                onClick={() => setTarefa(p => ({ ...p, descricao_tipo: 'texto', descricao: '', descricao_items: [] }))}>
                                Texto
                            </button>
                            <button className={`tf-tipo-btn ${tarefa.descricao_tipo === 'lista' ? 'active' : 'inactive'}`}
                                onClick={() => setTarefa(p => ({ ...p, descricao_tipo: 'lista', descricao: '', descricao_items: [] }))}>
                                Lista de Itens
                            </button>
                        </div>

                        {tarefa.descricao_tipo === 'texto' ? (
                            <textarea className="tf-input" style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                                value={tarefa.descricao}
                                onChange={e => setTarefa(p => ({ ...p, descricao: e.target.value }))}
                                placeholder="Adicione detalhes sobre a tarefa..." />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input className="tf-input" style={{ ...inputStyle, flex: 1 }} value={novoItem}
                                        onChange={e => setNovoItem(e.target.value)}
                                        placeholder="Digite um item e pressione Enter ou +"
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdicionarItem(); } }} />
                                    <button onClick={handleAdicionarItem} style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(44,203,144,0.12)', border: '1px solid rgba(44,203,144,0.25)', color: '#2CCB90', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <PlusIcon />
                                    </button>
                                </div>
                                {tarefa.descricao_items.length > 0 && (
                                    <div style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '10px', maxHeight: '140px', overflowY: 'auto' }}>
                                        {tarefa.descricao_items.map((item, idx) => (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: idx < tarefa.descricao_items.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                                    <span style={{ color: '#2CCB90', flexShrink: 0 }}><CheckIcon /></span>
                                                    <span style={{ fontSize: '13px', color: '#1e293b' }}>{item}</span>
                                                </div>
                                                <button onClick={() => setTarefa(p => ({ ...p, descricao_items: p.descricao_items.filter((_, i) => i !== idx) }))}
                                                    style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <MinusIcon />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Botões */}
                    <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
                        <button onClick={handleSubmit} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px 16px', borderRadius: '9px', background: tarefaEditando ? 'rgba(37,99,235,0.08)' : 'rgba(44,203,144,0.1)', border: `1px solid ${tarefaEditando ? 'rgba(37,99,235,0.2)' : 'rgba(44,203,144,0.25)'}`, color: tarefaEditando ? '#2563eb' : '#2CCB90', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                            {tarefaEditando ? <><EditIcon /> Atualizar Tarefa</> : <><PlusIcon /> Adicionar Tarefa</>}
                        </button>
                        {tarefaEditando && (
                            <button onClick={() => { onCancelarEdicao(); limpar(); }}
                                style={{ padding: '10px 16px', borderRadius: '9px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                                Cancelar
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default TarefaForm;
